// Создание договора
import React, { useEffect, useState } from "react";
import { Col, Row, Select, Checkbox, Input, Form, Button } from 'antd';

// Zustand store
import { useShallow } from 'zustand/react/shallow';
import { useCustomersOutstaff, useCompaniesStore, useProcuratory, useContractsStore } from 'storeZustand';

import { ArchiSpinner } from 'components';
import StarIcon from 'components/Common/Icons/StarIcon';

import AccordionContract from './accordionContract';
import useSelectedManager from 'hooks/useSelectedManager';
import useContractFields from 'hooks/contract/useContractFields';
import { useContractService } from 'hooks';

const ClientContractTab = ({ isEdit, selectedCity }) => {
	document.title = "Customer contract";

	// Все клиенты аутстафф
	const [
		customerOutstaffId, // id клиента
		setCustomerOutstaffId
	] = useCustomersOutstaff((state) => [
		state.customerOutstaffId,
		state.setCustomerOutstaffId
	]);

	// Договоры клиента
	const [
		contractById,
		addContract,
		updateContract,
		getTodayContracts,
		loadingSingle, // загрузка договора
	] = useContractsStore(useShallow((state) => [
		state.contractById,
		state.addContract,
		state.updateContract,
		state.getTodayContracts,
		state.loadingSingle
	]));

	// Все компании
	const [
		selectedCompany
	] = useCompaniesStore(useShallow((state) => [
		state.selectedCompany
	]));

	const [form] = Form.useForm();
	const fields = useContractFields(contractById);

	// Место подачи в уведомлении 
	const { name: fillingAddress = null } = selectedCompany?.address?.find(item => item?.type === 'Место подачи в уведомлении') || {};

	// сохранение id клиента
	useEffect(() => {
		const clientId = contractById?.client?.id;

		if (clientId && isEdit) {
			setCustomerOutstaffId(clientId);
		}
	}, [contractById]);

	// Список менеджеров для селекта
	const {
		selectedManager,
		outstaffManagerList,
		handleManagerChange,
	} = useSelectedManager(contractById ? contractById?.manager : null);

	// Список услуг для селекта
	const {
		contractServicesList,
		handleServiceChange,
		selectedContractService,
	} = useContractService(contractById ? contractById?.contract_service : []);

	const onFinish = async (values) => {
		if (!selectedManager) {
			return;
		}

		const id = isEdit ? contractById?.id : undefined;

		const updatedContract = {
			contract_number: values.contract_number, // Номер договора
			extending_number: values.extending_number, // Номер Доп соглашения
			contract_date: values.contract_date, // Дата договора
			extending_date: values.extending_date, // Дата Доп соглашения
			spravka_date: values.spravka_date, // Дата Справки
			work_start_date: values.work_start_date, // Дата начала работы
			work_end_date: values.work_end_date, // Дата окончания работы
			order_hire_number: values.order_hire_number, // Номер приказа о приеме
			order_hire_date: values.order_hire_date, // Дата Приема на работу
			order_fire_number: values.order_fire_number, // Номер приказа об увольнении
			order_fire_date: values.order_fire_date || null, // Дата увольнения
			start_period: values.start_period, // Начало периода
			end_period: values.end_period, // Конец периода
			kid_first_name: values.kid_first_name, // Имя ребенка
			kid_last_name: values.kid_last_name, // Фамилия ребенка
			kid_dob: values.kid_dob, // Дата рождения ребенка
			hodataystvo_date: values.hodataystvo_date, // Дата ходатайства
			client: customerOutstaffId ? customerOutstaffId : null, // Клиент 
			notification_date: values.notification_date, // Дата уведомления
			notification_place: fillingAddress, // Место уведомления
			manager: selectedManager, // Менеджер
			contract_service: selectedContractService || [], // Услуги договора
		};

		// если есть договор, то редактировать
		if (isEdit && contractById?.contract_number) {
			await updateContract({ id, updatedContract }); // обновление договора 
		} else {
			await addContract(updatedContract); // добавление договора
		}

		// обновить список договоров
		await getTodayContracts(selectedCity);
	};

	return (
		<div>
			{loadingSingle && <ArchiSpinner />}
			<Form
				fields={fields}
				onFinish={onFinish}
				autoComplete="off"
				form={form}
			>
				<Row className="mb-3" gutter={16}>
					{/* Номер договора */}
					<Col span={6}>
						<label className="form-label archi-label me-1">№ договора</label>
						<StarIcon />
						<Form.Item
							name="contract_number"
							rules={[
								{
									required: true,
									message: 'Введите номер договора!',
								},
							]}
						>
							<Input autoComplete='on' />
						</Form.Item>
					</Col>
					{/* Дата начала договора */}
					<Col span={6}>
						<label className="form-label me-1">Дата начала договора</label>
						<StarIcon />
						<Form.Item
							name="contract_date"
							rules={[
								{
									required: true,
									message: 'Введите дату договора!',
								},
							]}
						>
							<Input type="date" min="1900-01-01" max="2100-01-01" />
						</Form.Item>
					</Col>
					{/* Дата уведомления */}
					<Col span={6}>
						<label className="form-label">Дата уведомления</label>
						<Form.Item
							name="notification_date"
						>
							<Input type="date" min="1900-01-01" max="2100-01-01" />
						</Form.Item>
					</Col>
					{/* Дата увольнения */}
					<Col span={6}>
						<label className="form-label">Дата увольнения</label>
						<Form.Item
							name="order_fire_date"
						>
							<Input type="date" min="1900-01-01" max="2100-01-01" />
						</Form.Item>
					</Col>
				</Row>
				<Row className="mb-3 align-items-end" gutter={16}>
					<Col span={7}>
						<label className="form-label archi-label me-1">Менеджер</label>
						<StarIcon />
						{/* Выбор менеджера */}
						<Select
							placeholder="Выбрать"
							style={{ width: '100%' }}
							allowClear
							status={selectedManager ? 'success' : 'error'}
							value={selectedManager || undefined}
							onChange={handleManagerChange}
							options={outstaffManagerList}
						/>
					</Col>
					<Col span={6}>
						<label className="form-label archi-label me-1">Услуга</label>
						{/* Выбор услуги */}
						<Select
							placeholder="Выбрать"
							style={{ width: '100%' }}
							allowClear
							value={selectedContractService || undefined}
							onChange={handleServiceChange}
							options={contractServicesList}
						/>
					</Col>
				</Row>

				{/* Аккордионы договора */}
				<AccordionContract />

				<div className="text-end">
					<Form.Item noStyle={true}>
						<Button type="primary" htmlType="submit">
							{isEdit && contractById?.contract_number ? "Обновить договор" : "Создать договор"}
						</Button>
					</Form.Item>
				</div>
			</Form>
		</div>
	)
}

export default ClientContractTab;